/**
 * GamblerPanel -- Random item purchase at the gambler vendor.
 *
 * Choose by slot type (100g) or by minimum rarity (200g+).
 *
 * @module GamblerPanel
 */

import focusManager from '../../input/FocusManager.js';
import townSystem from '../../systems/TownSystem.js';
import { RARITY_COLORS, RARITY_NAMES } from '../../systems/LootSystem.js';
import { SLOT_INFO } from '../../systems/InventorySystem.js';

/** Slot types available for slot gamble. */
const GAMBLE_SLOTS = ['weapon', 'offhand', 'head', 'chest', 'legs', 'boots', 'gloves', 'ring', 'amulet'];

/** Rarity options for rarity gamble. */
const GAMBLE_RARITIES = ['magic', 'rare', 'unique'];

export default class GamblerPanel {
  constructor(options = {}) {
    this._party = options.party || null;
    this._townId = options.townId || null;
    this._onClose = options.onClose || null;
    this._el = null;
    this._isOpen = false;
    this._tab = 'slot'; // 'slot' or 'rarity'
    this._statusMsg = '';
    this._lastItem = null;
  }

  get isOpen() { return this._isOpen; }

  open() {
    if (this._isOpen) return;
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this.close();
    this._isOpen = true;

    this._el = document.createElement('div');
    this._el.className = 'town-panel-overlay';
    this._el.id = 'gambler-panel';
    this._el.setAttribute('role', 'dialog');
    this._el.setAttribute('aria-label', "Gambler's Den");
    this._el.setAttribute('data-scene', 'gambler');

    this._render();
    uiRoot.appendChild(this._el);
    focusManager.pushGroup('gambler', this._el);
  }

  close() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
      const topGroup = focusManager._currentGroup ? focusManager._currentGroup() : null;
      if (topGroup && topGroup.name === 'gambler') {
        focusManager.popGroup();
      }
    }
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    this._lastItem = null;
    if (this._onClose) this._onClose();
  }

  _render() {
    if (!this._el) return;
    this._el.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'town-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'town-panel-header';
    const title = document.createElement('h2');
    title.className = 'town-panel-title';
    title.textContent = "Gambler's Den";
    header.appendChild(title);

    const gold = document.createElement('span');
    gold.className = 'town-panel-gold';
    gold.textContent = `Gold: ${this._party?.gold || 0}`;
    header.appendChild(gold);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'town-panel-close';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'X';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Tabs
    const tabs = document.createElement('div');
    tabs.className = 'town-panel-tabs';

    const slotTab = document.createElement('button');
    slotTab.className = `town-panel-tab${this._tab === 'slot' ? ' town-panel-tab--active' : ''}`;
    slotTab.setAttribute('data-focusable', 'true');
    slotTab.textContent = 'By Slot (100g)';
    slotTab.addEventListener('click', () => { this._tab = 'slot'; this._render(); focusManager.refreshGroup(); });
    tabs.appendChild(slotTab);

    const rarityTab = document.createElement('button');
    rarityTab.className = `town-panel-tab${this._tab === 'rarity' ? ' town-panel-tab--active' : ''}`;
    rarityTab.setAttribute('data-focusable', 'true');
    rarityTab.textContent = 'By Rarity';
    rarityTab.addEventListener('click', () => { this._tab = 'rarity'; this._render(); focusManager.refreshGroup(); });
    tabs.appendChild(rarityTab);

    panel.appendChild(tabs);

    // Status / Last item
    if (this._statusMsg) {
      const status = document.createElement('div');
      status.className = 'town-panel-status';
      status.textContent = this._statusMsg;
      panel.appendChild(status);
    }

    if (this._lastItem) {
      const result = document.createElement('div');
      result.className = 'town-gamble-result';
      result.style.borderColor = RARITY_COLORS[this._lastItem.rarity] || RARITY_COLORS.normal;
      result.innerHTML = `
        <span style="color: ${RARITY_COLORS[this._lastItem.rarity]}">${this._lastItem.name}</span>
        <span class="town-item-meta">${RARITY_NAMES[this._lastItem.rarity]} ${(SLOT_INFO[this._lastItem.slot] || { name: this._lastItem.slot }).name}</span>
      `;
      panel.appendChild(result);
    }

    // Content
    const content = document.createElement('div');
    content.className = 'town-panel-content';

    if (this._tab === 'slot') {
      this._renderSlotGamble(content);
    } else {
      this._renderRarityGamble(content);
    }

    panel.appendChild(content);
    this._el.appendChild(panel);
  }

  _renderSlotGamble(container) {
    const costs = townSystem.getGambleCosts();
    const canAfford = (this._party?.gold || 0) >= costs.slotCost;

    const grid = document.createElement('div');
    grid.className = 'town-item-grid';

    for (const slot of GAMBLE_SLOTS) {
      const slotInfo = SLOT_INFO[slot] || { name: slot, icon: '?' };

      const card = document.createElement('div');
      card.className = `town-item-card${!canAfford ? ' town-item-card--disabled' : ''}`;
      card.setAttribute('data-focusable', 'true');

      const name = document.createElement('div');
      name.className = 'town-item-name';
      name.textContent = slotInfo.name || slot;
      card.appendChild(name);

      const price = document.createElement('div');
      price.className = 'town-item-price';
      price.textContent = `${costs.slotCost}g`;
      card.appendChild(price);

      if (canAfford) {
        card.addEventListener('click', () => {
          const result = townSystem.gambleBySlot(this._party, slot);
          this._statusMsg = result.message;
          this._lastItem = result.item;
          this._render();
          focusManager.refreshGroup();
        });
      }

      grid.appendChild(card);
    }

    container.appendChild(grid);
  }

  _renderRarityGamble(container) {
    const costs = townSystem.getGambleCosts();

    const grid = document.createElement('div');
    grid.className = 'town-item-grid';

    for (const rarity of GAMBLE_RARITIES) {
      const cost = costs.rarityCosts[rarity] || 200;
      const canAfford = (this._party?.gold || 0) >= cost;

      const card = document.createElement('div');
      card.className = `town-item-card${!canAfford ? ' town-item-card--disabled' : ''}`;
      card.setAttribute('data-focusable', 'true');

      const name = document.createElement('div');
      name.className = 'town-item-name';
      name.style.color = RARITY_COLORS[rarity];
      name.textContent = `${RARITY_NAMES[rarity]}+ Item`;
      card.appendChild(name);

      const price = document.createElement('div');
      price.className = 'town-item-price';
      price.textContent = `${cost}g`;
      card.appendChild(price);

      if (canAfford) {
        card.addEventListener('click', () => {
          const result = townSystem.gambleByRarity(this._party, rarity);
          this._statusMsg = result.message;
          this._lastItem = result.item;
          this._render();
          focusManager.refreshGroup();
        });
      }

      grid.appendChild(card);
    }

    container.appendChild(grid);
  }
}
